<script lang="ts">
  import { getCamelCaseBreakString, getKeyInObject, getSortedPropertyKeys, getTypeColors } from '../services/shared';
  import Expander from './expander.svelte';
  import InnerHtml from './inner-html.svelte';
  import ObjectToList from './object-to-list.svelte';
  import PropertyValue from './property-value.svelte';

  export let configuration: {
    propertiesOrder?: string[];
    excludedKeys?: string[];
    skipKeys?: string[];
    dontWrapInPropertiesKeys?: string[];
    specificExcludedValues?: {[key: string]: {keys: string[]; nestIndexes?: number[], replace?: {replaceKey: string; key: string, value: any}[]}};
    specificVisibleValues?: {[key: string]: {keys: string[]; nestIndexes?: number[]}};
    specificSkipKeys?: {[key: string]: {keys: string[]; nestIndexes?: number[]}};
    specificDontWrapInPropertiesKeys: {[key: string]: {keys: string[]; nestIndexes?: number[]}};
    oneOfKeys?: string[];
    excludedEntryKeys?: string[];
    codeKeys?: string[];
    colororizedKeys?: string[];
    expandableFrom?: number;
    expandableTo?: number;
  } = {
    propertiesOrder: [],
    excludedKeys: [],
    skipKeys: [],
    dontWrapInPropertiesKeys: [],
    specificExcludedValues: {},
    specificVisibleValues: {},
    specificSkipKeys: {},
    specificDontWrapInPropertiesKeys: {},
    oneOfKeys: [],
    excludedEntryKeys: [],
    codeKeys: [],
    colororizedKeys: [],
    expandableFrom: 1,
    expandableTo: Infinity
  };

  export let objectMap: any = null;

  export let key = '';
  $: {
    setExampleValue();
    excludedReplaceValue = getExcludeReplaceValue(key, parentKey, nestIndex);
    mapObject = getKeyInObject(parents, objectMap);
    calculatedeepestNestIndex();
    setOneOfParameters();
    calculateLongestKeyWidth();
  }

  export let value: any = null;
  export let parentKey = '';
  export let visualizedParentKey = '';
  export let parentValue: any = null;
  export let parents: string[] = parentKey ? [parentKey] : [];

  export let oneOfParameters: any = null;
  export let nestIndex = 0;
  export let KeyWidth = 0;

  export let onChildAction = (type: 'expand' | 'selectOneOf', actionValue: any) => {
    switch (type) {
      case 'expand':
        isExpanded = actionValue;
        break;
      case 'selectOneOf':
        selectedOneOfKey = actionValue;
        break;
    }
  };

  let mapObject: any = null;
  $: {
    if (mapObject) {
      setExpanded();
      setSelectedOneOfKey();
    }
  }

  let excludedReplaceValue: {replaceKey: string; key: string, value: any} | null = null;
  let deepestNestIndex = 0;
  let isExpanded = false;
  let selectedOneOfKey = '';

  const getShouldInclude = (itemKey: string, parentItemKey: string, iNest: number) => {
    const specificExcludedValues = configuration.specificExcludedValues;
    return (
      !configuration.excludedKeys.includes(itemKey) &&
      (
        !specificExcludedValues[parentItemKey]?.keys.includes(itemKey) ||
        specificExcludedValues[parentItemKey]?.nestIndexes &&
        !specificExcludedValues[parentItemKey].nestIndexes.includes(iNest)
      )
    );
  };

  const getShouldOnlyInclude = (itemKey: string, parentItemKey: string, iNest: number) => {
    const specificVisibleValues = configuration.specificVisibleValues;
    return (
      !specificVisibleValues[parentItemKey] ||
      (
        specificVisibleValues[parentItemKey]?.nestIndexes ?
        specificVisibleValues[parentItemKey]?.keys.includes(itemKey) || !specificVisibleValues[parentItemKey]?.nestIndexes.includes(iNest) :
        specificVisibleValues[parentItemKey]?.keys.includes(itemKey)
      )
    );
  };

  const getShouldSkip = (itemKey: string, parentItemKey: string, iNest: number) => {
    if (itemKey && parentItemKey && itemKey === parentItemKey) {
      return false;
    }

    const specificSkipKeys = configuration.specificSkipKeys;
    return (
      configuration.skipKeys.includes(itemKey) ||
      specificSkipKeys &&
      specificSkipKeys[parentItemKey]?.keys.includes(itemKey) &&
      (
        !specificSkipKeys[parentItemKey]?.nestIndexes ||
        specificSkipKeys[parentItemKey]?.nestIndexes?.includes(iNest)
      )
    );
  };

  const getWrapInProperties = (itemKey: string, parentItemKey: string, iNest: number) => {
    const specificDontWrapInPropertiesKeys = configuration.specificDontWrapInPropertiesKeys;
    return (
      !configuration.dontWrapInPropertiesKeys.includes(itemKey) &&
      (
        !specificDontWrapInPropertiesKeys[parentItemKey]?.keys.includes(itemKey) ||
        specificDontWrapInPropertiesKeys[parentItemKey]?.nestIndexes &&
        !specificDontWrapInPropertiesKeys[parentItemKey].nestIndexes.includes(iNest)
      )
    );
  };

  const getBrace = (type: string, isClosed = false) => {
    switch (type) {
      case 'array':
        return (isClosed ? ']' : '[');
      case 'object':
        return (isClosed ? '}': '{');
    }

    return '';
  };

  const setExampleValue = () => {
    const enumValue = value?.enum;
    if (enumValue && Array.isArray(enumValue) && !getExcludeReplaceValue('enum', key, nestIndex)) {
      value['example'] = key === 'items' ?
        enumValue.slice(0, enumValue.length > 4 ? 3 : 2) :
        enumValue[0];
    }
  };

  const getExcludeReplaceValue = (itemKey: string, parentItemKey: string, iNest: number) => {
    const specificExcludedValues = configuration.specificExcludedValues;
    if (
      specificExcludedValues[parentItemKey]?.replace?.length &&
      (
        specificExcludedValues[parentItemKey]?.keys.includes(itemKey) ||
        specificExcludedValues[parentItemKey]?.nestIndexes &&
        specificExcludedValues[parentItemKey].nestIndexes.includes(iNest)
      )
    ) {
      return specificExcludedValues[parentItemKey].replace.find(r => r.replaceKey === itemKey);
    }

    return null;
  };

  const calculatedeepestNestIndex = (itemKey = key, parentItemKey = parentKey, item = value, i = 0) => {
    if (
      item &&
      typeof item ==='object' &&
      !Array.isArray(item)
    ) {
      if (i >= deepestNestIndex) {
        deepestNestIndex = i;
      }

      i += 1;

      if (
        getShouldInclude(itemKey, parentItemKey, i) &&
        getShouldOnlyInclude(itemKey, parentItemKey, i)
      ) {
        for (const childItemKey in item) {
          calculatedeepestNestIndex(childItemKey, itemKey, item[childItemKey], i);
        }
      }
    }
  };

  const setOneOfParameters = () => {
    if (key === 'parameters' && value && value.oneOf) {
      oneOfParameters = { ...value.oneOf };
    }
  };

  const calculateLongestKeyWidth = () => {
    if (
      parentValue &&
      typeof parentValue ==='object' &&
      !Array.isArray(parentValue)
    ) {
      let i = 0;
      const itemLength = Object.keys(parentValue).length;
      let longestKeyWidth = 0;

      for (const valueKey in parentValue) {
        i += 1;
        const width = valueKey.length * 8.42;
        if (width > longestKeyWidth) { longestKeyWidth = width; }
        if (i === itemLength) { KeyWidth = longestKeyWidth < 66 ? 78 : longestKeyWidth + 30; }
      }
    }
  };

  const setExpanded = () => {
    if (deepestNestIndex > 0) {
      isExpanded = !!mapObject[key]?.expanded;
      onChildAction('expand', isExpanded);
    }
  };

  const expand = () => {
    const oneOfKeys = configuration.oneOfKeys;
    const oneOfIndex = parents.findIndex(p => oneOfKeys.includes(p));
    if (
      parents.length - 1 - oneOfIndex < 2 &&
      oneOfKeys.some(item => parents.includes(item)) &&
      getShouldSkip(key, parentKey, nestIndex)
    ) {
      const oneOfObjectMap = getKeyInObject(parents.slice(0, oneOfIndex + 1), objectMap);
      for (const mapKey in oneOfObjectMap) {
        if (oneOfObjectMap[mapKey].properties) {
          oneOfObjectMap[mapKey].properties.expanded = !oneOfObjectMap[mapKey].properties?.expanded;
        }
      }
      mapObject = { ...mapObject };
    } else {
      mapObject[key].expanded = !mapObject[key].expanded;
    }
  };

  const setSelectedOneOfKey = () => {
    if (configuration.oneOfKeys.includes(key)) {
      const valueKeys = value ? Object.keys(value) : [];
      selectedOneOfKey = valueKeys[0] ?? '';

      for (const valueKey of valueKeys) {
        if (!!mapObject[key][valueKey]?.selected) {
          selectedOneOfKey = valueKey;
        }
      }

      onChildAction('selectOneOf', selectedOneOfKey);
    }
  };

  const selectOneOfKey = (itemKey: string) => {
    for (const valueKey in value) {
      if (mapObject[key][valueKey]?.selected) {
        mapObject[key][valueKey].selected = false;
      }
    }

    mapObject[key][itemKey].selected = true;
  };
</script>

{#if nestIndex > 0 || value !== null}
  {#if getShouldInclude(key, parentKey, nestIndex) && getShouldOnlyInclude(key, parentKey, nestIndex)}
    {#if value && typeof value ==='object'}
      {#if Array.isArray(value)}
      <!-- ARRAY VISUALIZATION -->
        <div class="propertyEntryWrapper">
          {#if key}<span>{key}:</span>{/if}
          <code class="codeFrame">
            {#if visualizedParentKey && key !== 'enum'}{visualizedParentKey}={#if key !== 'enum'}[{/if}{/if}{#each value as entry, i}{#if typeof entry === 'string'}"{/if}{#if entry && typeof entry === 'object'}<ObjectToList value={entry} parentValue={value} index={i} nestIndex={1}/>{:else}{entry}{/if}{#if typeof entry === 'string'}"{/if}{#if typeof entry !== 'object' && i < value.length - 1}{', '}{/if}{/each}{#if key !== 'enum'}]{/if}
          </code>
        </div>
      {:else}{#if configuration.oneOfKeys.includes(key)}
      <!-- ONE OF SELECTION -->
        <div class="selection">
          <div class="propertyEntryWrapper">One of</div>
          <div class="selection__list">
            {#each Object.keys(value) as valueKey}
              <div
                class="chip {valueKey === selectedOneOfKey ? 'chip--selected' : null}"
                on:click={() => valueKey === selectedOneOfKey ? null : selectOneOfKey(valueKey)}
              >{valueKey}</div>
            {/each}
          </div>
          {#if selectedOneOfKey}
            <PropertyValue
              configuration={
                {
                  ...configuration,
                  specificSkipKeys: {
                    ...configuration.specificSkipKeys,
                    [key]: {
                      keys: [selectedOneOfKey],
                      nestIndexes: [nestIndex]
                    }
                  },
                  specificExcludedValues: {
                    ...configuration.specificExcludedValues,
                    filterType: {
                      keys: ['enum'],
                      nestIndexes: [nestIndex + 1],
                      replace: [{
                        replaceKey: 'enum',
                        key: 'value',
                        value: selectedOneOfKey
                      }]
                    }
                  },
                  specificVisibleValues: {
                    ...configuration.specificVisibleValues,
                    [selectedOneOfKey]: { keys: ['description', 'properties'], nestIndexes: [nestIndex] }
                  },
                  specificDontWrapInPropertiesKeys: {
                    ...configuration.specificDontWrapInPropertiesKeys,
                    [key]: { keys: [selectedOneOfKey], nestIndexes: [nestIndex] }
                  }
                }
              }
              {objectMap}
              key={selectedOneOfKey}
              value={value[selectedOneOfKey]}
              parentKey={key}
              visualizedParentKey={parentKey}
              parentValue={value}
              parents={key ? [...parents, key] : parents}
              {nestIndex}
              {KeyWidth}
              {onChildAction}
            />
          {/if}
        </div>
      {:else}{#if getShouldSkip(key, parentKey, nestIndex)}
      <!-- SKIP KEY (-> key will not appear in table's key section) -->
        {#if getWrapInProperties(key, parentKey, nestIndex)}
          {#if configuration.expandableFrom <= nestIndex && configuration.expandableTo >= nestIndex}
            <Expander {isExpanded} {expand}/>
          {/if}
          {#if !(configuration.expandableFrom <= nestIndex && configuration.expandableTo >= nestIndex) || isExpanded}
            <div class="properties">
              {#each Object.keys(value) as valueKey}
                <PropertyValue
                  {configuration}
                  {objectMap}
                  key={valueKey}
                  value={value[valueKey]}
                  parentKey={key}
                  parentValue={value}
                  parents={key ? [...parents, key] : parents}
                  {oneOfParameters}
                  {nestIndex}
                  {KeyWidth}
                />
              {/each}
            </div>
          {/if}
        {:else}
          {#each Object.keys(value) as valueKey}
            <PropertyValue
              {configuration}
              {objectMap}
              key={valueKey}
              value={value[valueKey]}
              parentKey={key}
              visualizedParentKey={parentKey}
              parentValue={value}
              parents={key ? [...parents, key] : parents}
              {oneOfParameters}
              {nestIndex}
              {KeyWidth}
              {onChildAction}
            />
          {/each}
        {/if}
      {:else}
      <!-- TABLE ROW -->
        <div class="property property--nestIndex-{nestIndex}">
          <div
            class="property__key {deepestNestIndex > 0 && isExpanded ? 'property__key--sticky' : null}"
            style="{KeyWidth ? 'max-width: ' + (KeyWidth) + 'px' : null}"
          >
            <div class="propertyEntryWrapper">
              <div class="propertyEntryWrapper__keyRow">
                <InnerHtml text={getCamelCaseBreakString(key)}/>
                <div></div>
                {#if deepestNestIndex > 1 && isExpanded}<code>{getBrace(value.type)}</code>{/if}
              </div>

              {#if value?.required}
              <!-- REQUIRED -->
                <span>required</span>
              {/if}

              {#if value?.allOf?.if?.properties}
              <!-- ALL OF -->
                <span>
                  <span>Only if</span>
                  {#each Object.entries(value.allOf.if.properties) as [ifKey, ifValue]}
                    <InnerHtml text={getCamelCaseBreakString(ifKey) + ': ' + ifValue['const']} />
                  {/each}
                </span>
              {/if}

              {#if oneOfParameters && Object.keys(oneOfParameters).includes(key)}
              <!-- ONE OF -->
                <span>
                  <span>One of</span>
                  {#each Object.keys(oneOfParameters) as oneOfKey, i}
                    <InnerHtml text={getCamelCaseBreakString(oneOfKey) + (i < Object.keys(oneOfParameters).length - 1 ? ', ' : '')} />
                  {/each}
                </span>
              {/if}
            </div>

            <div class="line">
              {#if deepestNestIndex > 1 && isExpanded}
                <div class="propertyEntryWrapper__keyRow"><code>{getBrace(value.type, true)}</code></div>
              {/if}
            </div>
          </div>

          <div class="property__value">
            {#each getSortedPropertyKeys(Object.keys(value), configuration.propertiesOrder) as valueKey}
              <PropertyValue
                {configuration}
                {objectMap}
                key={valueKey}
                value={value[valueKey]}
                parentKey={key}
                visualizedParentKey={key}
                parentValue={value}
                parents={key ? [...parents, key] : parents}
                nestIndex={nestIndex + 1}
                {KeyWidth}
                {onChildAction}
              />
            {/each}
          </div>
        </div>
      {/if}{/if}{/if}
    {:else}
    <!-- ENTRIES -->
      <div class="propertyEntryWrapper propertyEntryWrapper--{key}">
        {#if !configuration.excludedEntryKeys.includes(key)}
        <!-- ENTRY KEY -->
          <span>{key}:</span>
        {/if}

        <!-- ENTRY VALUE -->
        {#if configuration.codeKeys.includes(key)}
        <!-- CODE -->
          <code class="codeFrame">
            {#if visualizedParentKey}{visualizedParentKey}={/if}{#if parentKey === 'items'}[{#if typeof value === 'string'}"{/if}{/if}{value}{#if parentKey === 'items'}{#if typeof value === 'string'}"{/if}]{/if}
          </code>
        {:else}
        <!-- DEFAULT -->
          <span
            style={configuration.colororizedKeys.includes(key) ? 'color: ' + getTypeColors(value) : null}
            contenteditable="false"
          >
            <InnerHtml
              text="{value + ''}"
              htmlType="span"
            />
          </span>
        {/if}

        <!-- TYPE EXTENSIONS -->
        {#if key === 'type'}
          {#if parentValue?.items?.type}
          <!-- ARRAY EXTENSION (e.g. array of strings) -->
            of
            <span style={configuration.colororizedKeys.includes(key) ? 'color: ' + getTypeColors(parentValue.items.type) : null}>
              {parentValue.items.type}s
            </span>
          {/if}

          <!-- VALUE SET -->
          {#if parentValue?.minimum !== undefined || parentValue?.items?.minimum !== undefined}
            {#if parentValue.exclusiveMinimum || parentValue.items?.exclusiveMinimum}
            <!-- > or ≥ -->
              &#062;
            {:else}
              &geq;
            {/if}
            {parentValue.items?.minimum !== undefined ? parentValue.items.minimum + ' items' : parentValue.minimum}
          {/if}
          {#if parentValue?.maximum !== undefined}
          <!-- ≤ -->
            &le;
            {parentValue.maximum}
            {#if parentValue.items && parentValue.items.minimum === undefined}
              items
            {/if}
          {/if}
        {/if}
      </div>
    {/if}
  {:else}{#if !!excludedReplaceValue}
  <!-- REPLACE SPECIFIC VALUES -->
    <PropertyValue
      {configuration}
      {objectMap}
      key={excludedReplaceValue.key}
      value={excludedReplaceValue.value}
      parentKey={excludedReplaceValue.replaceKey !== excludedReplaceValue.key ? parentKey : ''}
      {visualizedParentKey}
      {parentValue}
      {parents}
      {nestIndex}
      {KeyWidth}
      {onChildAction}
    />
  {/if}{/if}
{/if}
